import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { Snackbar, Alert, Slide, IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

const SlideTransition = (props) => {
  return <Slide {...props} direction="up" />;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  // Track timeouts for cleanup on unmount
  const timeoutRefs = useRef(new Map());

  // Cleanup all timeouts on unmount
  useEffect(() => {
    const timeouts = timeoutRefs.current;
    return () => {
      timeouts.forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
      timeouts.clear();
    };
  }, []);

  const showToast = useCallback((message, severity = 'info', options = {}) => {
    const id = Date.now();
    const newToast = {
      id,
      message,
      severity,
      duration: options.duration || 6000,
      action: options.action,
      persist: options.persist || false,
    };

    setToasts((prev) => [...prev, newToast]);

    if (!options.persist) {
      const timeoutId = setTimeout(() => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
        // Clean up the timeout reference after it fires
        timeoutRefs.current.delete(id);
      }, newToast.duration);
      // Store timeout reference for cleanup
      timeoutRefs.current.set(id, timeoutId);
    }

    return id;
  }, []);

  const hideToast = useCallback((id) => {
    // Clear the timeout if toast is manually dismissed
    const timeoutId = timeoutRefs.current.get(id);
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutRefs.current.delete(id);
    }
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showSuccess = useCallback((message, options) => {
    return showToast(message, 'success', options);
  }, [showToast]);

  const showError = useCallback((message, options) => {
    return showToast(message, 'error', { duration: 8000, ...options });
  }, [showToast]);

  const showWarning = useCallback((message, options) => {
    return showToast(message, 'warning', options);
  }, [showToast]);

  const showInfo = useCallback((message, options) => {
    return showToast(message, 'info', options);
  }, [showToast]);

  const value = {
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    hideToast,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      {toasts.map((toast, index) => (
        <Snackbar
          key={toast.id}
          open={true}
          autoHideDuration={toast.persist ? null : toast.duration}
          onClose={() => hideToast(toast.id)}
          TransitionComponent={SlideTransition}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          sx={{ 
            bottom: (theme) => theme.spacing(2 + index * 9),
            transition: 'bottom 0.3s ease-in-out',
          }}
        >
          <Alert
            severity={toast.severity}
            sx={{ 
              width: '100%',
              minWidth: '300px',
              boxShadow: 3,
              '& .MuiAlert-message': {
                fontSize: '0.95rem',
              }
            }}
            action={
              <>
                {toast.action}
                <IconButton
                  size="small"
                  aria-label="close"
                  color="inherit"
                  onClick={() => hideToast(toast.id)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </>
            }
          >
            {toast.message}
          </Alert>
        </Snackbar>
      ))}
    </ToastContext.Provider>
  );
};